import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { ClientRender } from '@change-corgi/core/react/ssr/render';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { StartAPetitionPageCombinedState, StartAPetitionPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { StartAPetitionPageDataContextProvider } from './pageData';
import { StepStateProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<StartAPetitionPageCombinedState> };

type Props = {
	prefetchedData: StartAPetitionPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function StartAPetitionPageContextProvider({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<StartAPetitionPageDataContextProvider pageData={pageData}>
			{/* StepState relies on local storage so is only initialized on the client, so no SSR :( */}
			<ClientRender>
				<StepStateProvider>{children}</StepStateProvider>
			</ClientRender>
		</StartAPetitionPageDataContextProvider>
	);
}
